import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Bot, User, Sparkles, MessageSquare, Users, Clock, Smile, Activity, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { api, shortId } from "../lib/api";
import { chatSuggestions } from "../lib/mockData";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";
import DiscoveryCard from "../components/common/DiscoveryCard";

const DemoHeader = ({ n, title, blurb }) => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo {n}</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">{title}</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">{blurb}</p>
  </div>
);

const KPI = ({ icon: Icon, label, value, sub }) => (
  <Card className="glass">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4"/></div>
      </div>
      <div className="mt-2 font-display text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

const ChatBubble = ({ role, text }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
    className={`flex gap-2 ${role === "user" ? "justify-end" : "justify-start"}`}
  >
    {role !== "user" && (
      <Avatar className="w-8 h-8 shrink-0"><AvatarFallback className="bg-primary text-primary-foreground text-xs"><Bot className="w-4 h-4"/></AvatarFallback></Avatar>
    )}
    <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm
      ${role === "user"
        ? "bg-primary text-primary-foreground rounded-br-sm"
        : "bg-muted text-foreground rounded-bl-sm"}`}>
      {text}
    </div>
    {role === "user" && (
      <Avatar className="w-8 h-8 shrink-0"><AvatarFallback className="bg-accent text-accent-foreground text-xs"><User className="w-4 h-4"/></AvatarFallback></Avatar>
    )}
  </motion.div>
);

const TypingBubble = () => (
  <div className="flex gap-2 justify-start">
    <Avatar className="w-8 h-8 shrink-0"><AvatarFallback className="bg-primary text-primary-foreground text-xs"><Bot className="w-4 h-4"/></AvatarFallback></Avatar>
    <div className="px-4 py-3 rounded-2xl bg-muted rounded-bl-sm">
      <span className="typing-dot"/><span className="typing-dot"/><span className="typing-dot"/>
    </div>
  </div>
);

const GREETING = {
  web: "Hello 👋 I'm Ama, StaffLink Ghana's discovery consultant. May I start by asking your name?",
  whatsapp: "Akwaaba! 🌟 I'm Ama from StaffLink Ghana. May I start by asking your name?",
};

const ChatWindow = ({ variant = "web", onDiscovery }) => {
  const storageKey = `sl-chat-session-${variant}`;
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return shortId();
    return localStorage.getItem(storageKey) || (() => {
      const id = shortId();
      localStorage.setItem(storageKey, id);
      return id;
    })();
  });
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING[variant] }]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const scrollRef = useRef(null);

  // Load prior history from server on mount so conversation state survives reloads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chat/${sessionId}/history`);
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data.map((d) => ({ role: d.role, text: d.text })));
        }
      } catch { /* first-time session — keep greeting */ }
      try {
        const { data } = await api.get(`/chat/${sessionId}/discovery`);
        if (!cancelled && data) onDiscovery?.(data.discovery || null, data.lead_score ?? null);
      } catch { /* no discovery yet */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pending]);

  const send = async (text) => {
    const val = (text ?? input).trim();
    if (!val || pending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: val }]);
    setPending(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, message: val });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      onDiscovery?.(data.discovery || null, data.lead_score ?? null);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setPending(false);
    }
  };

  const resetConversation = async () => {
    if (pending || resetting) return;
    setResetting(true);
    try {
      await api.delete(`/chat/${sessionId}`);
    } catch { /* even if server delete fails, we reset the client */ }
    const fresh = shortId();
    localStorage.setItem(storageKey, fresh);
    setSessionId(fresh);
    setMessages([{ role: "assistant", text: GREETING[variant] }]);
    onDiscovery?.(null, null);
    setResetting(false);
  };

  const isWa = variant === "whatsapp";
  return (
    <div className={`rounded-2xl overflow-hidden border border-border ${isWa ? "bg-[#ECE5DD] dark:bg-slate-800" : "bg-card"}`} data-testid={`chat-${variant}`}>
      <div className={`px-4 py-3 flex items-center gap-3 ${isWa ? "bg-[#075E54] text-white" : "bg-primary text-primary-foreground"}`}>
        <Avatar className="w-9 h-9"><AvatarFallback className="bg-white/20 text-white"><Bot className="w-4 h-4"/></AvatarFallback></Avatar>
        <div className="flex-1">
          <div className="text-sm font-semibold">Ama · StaffLink AI</div>
          <div className="text-[11px] opacity-80 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-accent"/> Online · replies instantly
          </div>
        </div>
        <Badge className="bg-white/20 hover:bg-white/20 text-white border-none text-[10px]">{isWa ? "WHATSAPP" : "WEB"}</Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={resetConversation}
          disabled={resetting || pending}
          className="h-8 gap-1 text-white hover:bg-white/15 hover:text-white"
          data-testid={`chat-reset-${variant}`}
          title="Start a new conversation"
        >
          <RotateCcw className="w-3.5 h-3.5"/> <span className="hidden sm:inline text-xs">Reset</span>
        </Button>
      </div>
      <div ref={scrollRef} className="p-4 space-y-3 h-[420px] overflow-y-auto">
        {messages.map((m, i) => <ChatBubble key={i} {...m}/>)}
        {pending && <TypingBubble/>}
      </div>
      <div className="p-3 border-t border-border bg-background/60">
        <div className="flex gap-2 mb-2 overflow-x-auto">
          {chatSuggestions.slice(0, 3).map((s) => (
            <Button key={s} size="sm" variant="outline" className="text-xs whitespace-nowrap" onClick={() => send(s)} data-testid={`chat-suggestion-${variant}`}>{s}</Button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…" data-testid={`chat-input-${variant}`}/>
          <Button type="submit" disabled={pending} data-testid={`chat-send-${variant}`}><Send className="w-4 h-4"/></Button>
        </form>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [convs, setConvs] = useState([]);
  const [stats, setStats] = useState(null);

  const load = async () => {
    const [{ data: c }, { data: s }] = await Promise.all([
      api.get("/chat/admin/conversations"),
      api.get("/chat/admin/stats"),
    ]);
    setConvs(c);
    setStats(s);
  };

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KPI icon={MessageSquare} label="Conversations" value={stats?.total_sessions ?? "—"} sub={`${stats?.total_messages ?? 0} messages`}/>
        <KPI icon={Users} label="Active now" value={stats?.active_now ?? "—"} sub="Real-time"/>
        <KPI icon={Smile} label="CSAT" value={`${stats?.csat ?? 96}%`} sub="Customer satisfaction"/>
        <KPI icon={Clock} label="Avg response" value={`${stats?.avg_response_seconds ?? 1.4}s`} sub="Ama typing time"/>
      </div>

      <Card className="glass"><CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Live conversations</div>
            <div className="font-display text-lg font-semibold">Recent chats</div>
          </div>
          <Badge variant="outline" className="gap-1"><Activity className="w-3 h-3"/> auto-refresh</Badge>
        </div>
        {convs.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">No conversations yet — go say hi to Ama on the left!</div>
        ) : (
          <div className="divide-y divide-border">
            {convs.map((c) => (
              <div key={c.session_id} className="py-3 flex items-center gap-4">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-bold">
                  {c.session_id.slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.visitor_name || `Visitor ${c.session_id.slice(0,6)}`}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.preview || "…"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-semibold">{c.messages_count} msgs</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(c.last_at).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      <Card className="glass"><CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold">Most-asked topics</div>
          <Badge variant="secondary" className="gap-1"><Sparkles className="w-3 h-3"/> AI-detected</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {(stats?.top_topics ?? []).length === 0
            ? <div className="text-sm text-muted-foreground">Start chatting to build insights.</div>
            : (stats.top_topics.map((t) => (
              <Badge key={t.term} variant="outline" className="capitalize">{t.term} · {t.count}</Badge>
            )))}
        </div>
      </CardContent></Card>
    </div>
  );
};

export default function Demo1Receptionist() {
  const [discovery, setDiscovery] = useState(null);
  const [leadScore, setLeadScore] = useState(null);
  const handleDiscovery = (d, s) => { setDiscovery(d); setLeadScore(s); };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader
        n={1}
        title="AI Receptionist & WhatsApp Automation"
        blurb="Meet Ama — a real Claude-powered discovery consultant who qualifies leads, books consultations and hands off to your team. Every conversation is stored, analysed and scored in real-time."
      />
      <Tabs defaultValue="web" className="w-full">
        <TabsList data-testid="chat-mode-tabs">
          <TabsTrigger value="web" data-testid="tab-web">Website chat</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp view</TabsTrigger>
        </TabsList>
        <TabsContent value="web">
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
            <div className="lg:col-span-2"><ChatWindow variant="web" onDiscovery={handleDiscovery}/></div>
            <div className="lg:col-span-2"><DiscoveryCard discovery={discovery} leadScore={leadScore}/></div>
            <div className="lg:col-span-2"><AdminPanel/></div>
          </div>
        </TabsContent>
        <TabsContent value="whatsapp">
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
            <div className="lg:col-span-2"><ChatWindow variant="whatsapp" onDiscovery={handleDiscovery}/></div>
            <div className="lg:col-span-2"><DiscoveryCard discovery={discovery} leadScore={leadScore}/></div>
            <div className="lg:col-span-2"><AdminPanel/></div>
          </div>
        </TabsContent>
      </Tabs>

      <BeforeAfter demoId={1}/>
      <ConsultationCTA demoLabel="AI Receptionist & WhatsApp"/>
    </div>
  );
}
